import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/utils/toast";
import { useTranslation } from "react-i18next";

const consumptionFactorSchema = z.object({
  year: z.coerce.number().min(1900).max(2100),
  fuel_type: z.number(),
  vehicle_type: z.number(),
  coefficient: z.coerce.number().min(0),
});

type ConsumptionFactorFormValues = z.infer<typeof consumptionFactorSchema>;

interface ConsumptionFactorFormProps {
  id?: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface SelectOption {
  id: number;
  name: string;
}

interface SelectResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SelectOption[];
}

export function ConsumptionFactorForm({
  id,
  onSuccess,
  onCancel,
}: ConsumptionFactorFormProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const isEditMode = !!id;

  const form = useForm<ConsumptionFactorFormValues>({
    resolver: zodResolver(consumptionFactorSchema),
    defaultValues: {
      year: new Date().getFullYear(),
      fuel_type: undefined,
      vehicle_type: undefined,
      coefficient: 0,
    },
  });

  // Fetch fuel types for the select
  const { data: fuelTypesData } = useQuery<SelectResponse>({
    queryKey: ["fuel-types"],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl("fuel-types/?limit=9999&category=Vehicle"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error(t("coefficients.consumption.error.fetchFuelTypes"));
      }
      return response.json();
    },
  });

  // Fetch vehicle types for the select
  const { data: vehicleTypesData } = useQuery<SelectResponse>({
    queryKey: ["vehicle-types"],
    queryFn: async () => {
      const response = await fetch(getApiUrl("vehicle-types/?limit=9999"), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(t("coefficients.consumption.error.fetchVehicleTypes"));
      }
      return response.json();
    },
  });

  // Fetch consumption factor data if in edit mode
  const { data: consumptionFactorData, isLoading } = useQuery({
    queryKey: ["consumption-factors", id],
    queryFn: async () => {
      if (!id) return null;
      const response = await fetch(getApiUrl(`consumption-factors/${id}/`), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(
          t("coefficients.consumption.error.fetchConsumptionFactor"),
        );
      }
      return response.json();
    },
    enabled: isEditMode,
  });

  // Set form values when data is loaded
  useEffect(() => {
    if (consumptionFactorData) {
      form.reset({
        year: consumptionFactorData.year,
        fuel_type: consumptionFactorData.fuel_type?.id,
        vehicle_type: consumptionFactorData.vehicle_type?.id,
        coefficient: parseFloat(consumptionFactorData.coefficient),
      });
    }
  }, [consumptionFactorData, form]);

  const mutation = useMutation({
    mutationFn: async (values: ConsumptionFactorFormValues) => {
      const url = id
        ? getApiUrl(`consumption-factors/${id}/`)
        : getApiUrl("consumption-factors/");
      const method = id ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...values,
          fuel_type: values.fuel_type,
          vehicle_type: values.vehicle_type,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to save consumption factor");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["consumption-factors"] });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(error.message);
      console.error(error);
    },
  });

  const onSubmit = (values: ConsumptionFactorFormValues) => {
    mutation.mutate(values);
  };

  if (isLoading && isEditMode) {
    return <div>{t("common.loading")}</div>;
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col h-full"
      >
        <div className="space-y-6 flex-1 overflow-y-auto pb-20">
          <FormField
            control={form.control}
            name="year"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("coefficients.consumption.form.year")}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1900}
                    max={2100}
                    {...field}
                    onChange={(e) => field.onChange(parseInt(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="fuel_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("coefficients.consumption.form.fuelType")}
                </FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(parseInt(value))}
                  value={field.value?.toString()}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          "coefficients.consumption.form.selectFuelType",
                        )}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {fuelTypesData?.results.map((fuelType) => (
                      <SelectItem
                        key={fuelType.id}
                        value={fuelType.id.toString()}
                      >
                        {fuelType.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="vehicle_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("coefficients.consumption.form.vehicleType")}
                </FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(parseInt(value))}
                  value={field.value?.toString()}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          "coefficients.consumption.form.selectVehicleType",
                        )}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {vehicleTypesData?.results.map((vehicleType) => (
                      <SelectItem
                        key={vehicleType.id}
                        value={vehicleType.id.toString()}
                      >
                        {vehicleType.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="coefficient"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("coefficients.consumption.form.coefficient")}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseFloat(e.target.value) || 0)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4 flex justify-end space-x-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={mutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button variant="default" type="submit" loading={mutation.isPending}>
            {isEditMode ? t("common.update") : t("common.create")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
